import { uuidv4 } from '../../utils/uuid';

/**
 * Options for a thunk whose request carries an idempotency key.
 *
 * The thunk's `requestId` *is* the key: a fresh one for every new action, and
 * the one passed in `arg.idempotencyKey` when the operator retries the same
 * action after losing the answer — so the server registers it once. Neither
 * the screens nor the reducers make keys up.
 *
 * The condition keeps two sends of the same key from being in flight at once.
 */
export const idempotentThunkOptions = {
  idGenerator: (arg) => arg?.idempotencyKey ?? uuidv4(),
  condition: (arg, { getState }) =>
    !(arg?.idempotencyKey && getState().feedback?.inFlight?.[arg.idempotencyKey]),
};

/**
 * The argument that sends an action again under the key it was first sent with.
 *
 * @param {{arg: object, key: string}} write - What the slice, or the band, kept of it.
 */
export const sameAction = ({ arg, key }) => ({ ...arg, idempotencyKey: key });

/** The argument as the operator meant it, without the key a retry was given. */
export const intentOf = (arg) => {
  const { idempotencyKey, ...rest } = arg || {};
  return rest;
};

const sameIntent = (a, b, meaning) => JSON.stringify(meaning(a)) === JSON.stringify(meaning(b));

/**
 * The write a flow kept because its answer was lost, when `arg` is that same
 * action again; `null` otherwise.
 *
 * @param {Function} thunk - A keyed write.
 * @param {object} arg
 * @param {{lastWrite: ?{type: string, arg: object, key: string}, pending: *}} flow - The flow's slice.
 * @param {(arg: object) => object} [meaning] - What of the argument makes it the same action; all but its key by default.
 */
export const lostWriteOf = (thunk, arg, { lastWrite, pending }, meaning = intentOf) =>
  !pending && lastWrite?.type === thunk.typePrefix && sameIntent(lastWrite.arg, arg, meaning) ? lastWrite : null;

/**
 * Sends a keyed write from its screen. The same action sent again while the
 * answer to its last send is lost — the operator taps «Pickear» instead of
 * «Reintentar» — goes under the key it was first sent with, so the server
 * registers it once. A different action gets a key of its own.
 *
 * @param {Function} thunk - A keyed write.
 * @param {object} arg
 * @param {(state: object) => {lastWrite: ?{type: string, arg: object, key: string}, pending: *}} selectFlow
 */
export const sendWrite = (thunk, arg, selectFlow) => (dispatch, getState) => {
  const lost = lostWriteOf(thunk, arg, selectFlow(getState()));
  return dispatch(thunk(lost ? sameAction(lost) : arg));
};
